<template>
  <!-- Teleported to body: keeps position:fixed working when high-contrast filter is applied to #app,
       and stays outside Tailwind's important:'#app' scope (styled with plain CSS below). -->
  <Teleport to="body">
    <div class="acc-widget" dir="rtl" @keydown.esc="closePanel">

      <button class="acc-btn"
              :class="{ 'acc-btn-active': panel_open || anyActive }"
              :aria-label="t.button_aria"
              :aria-expanded="panel_open ? 'true' : 'false'"
              :title="t.button_title"
              @click="togglePanel">
        <!-- אייקון נגישות סטנדרטי (דמות) -->
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm9 5.5a1 1 0 0 1-.79 1.18l-5.21 1.04v2.07l2.9 6.96a1 1 0 0 1-1.85.77L13.5 13.9h-3l-2.55 5.62a1 1 0 1 1-1.85-.77l2.9-6.96V9.72L3.79 8.68A1 1 0 0 1 4.2 6.72l6.28 1.26h3.04l6.28-1.26A1 1 0 0 1 21 7.5z"/>
        </svg>
      </button>

      <div v-if="panel_open" class="acc-panel" role="dialog" aria-labelledby="acc-panel-title">
        <div class="acc-panel-head">
          <h3 id="acc-panel-title">{{ t.panel_title }}</h3>
          <button class="acc-close" :aria-label="t.close_aria" @click="closePanel">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18"/>
            </svg>
          </button>
        </div>

        <div class="acc-options">
          <div v-for="opt in options" :key="opt.key" class="acc-option">
            <label class="acc-option-row" :for="'acc-' + opt.key">
              <span class="acc-option-label">{{ t[opt.key] }}</span>
              <input type="checkbox"
                     :id="'acc-' + opt.key"
                     :checked="prefs[opt.key]"
                     :aria-describedby="'acc-desc-' + opt.key"
                     @change="toggleOption(opt.key)" />
            </label>
            <p class="acc-option-desc" :id="'acc-desc-' + opt.key">{{ t[opt.key + '_desc'] }}</p>
          </div>
        </div>

        <div class="acc-panel-foot">
          <button class="acc-reset" @click="resetAll">{{ t.reset }}</button>
        </div>
      </div>

    </div>
  </Teleport>
</template>

<script>
const STORAGE_KEY = 'accessibility-preferences'
const STYLE_ID = 'accessibility-widget-styles'

// המחרוזות מרוכזות כאן במבנה מפתח→טקסט, מוכן להחלפה ב-i18n אם יתווסף לפרויקט (האתר עברית בלבד)
const STRINGS = {
  button_aria: 'פתח תפריט נגישות',
  button_title: 'נגישות',
  panel_title: 'הגדרות נגישות',
  close_aria: 'סגור תפריט נגישות',
  high_contrast: 'ניגודיות גבוהה',
  high_contrast_desc: 'הגברת הניגודיות לקריאה טובה יותר',
  large_text: 'טקסט גדול',
  large_text_desc: 'הגדלת גודל הטקסט',
  underline_links: 'קו תחתון לקישורים',
  underline_links_desc: 'הוספת קו תחתון לכל הקישורים',
  focus_highlight: 'הדגשת פוקוס',
  focus_highlight_desc: 'הדגשה ברורה יותר של רכיבים בפוקוס',
  stop_animations: 'הפסקת אנימציות',
  stop_animations_desc: 'הפסקת כל האנימציות והמעברים',
  reset: 'איפוס הגדרות נגישות'
}

const GLOBAL_CSS = `
html.acc-large-text { font-size: 120% !important; }
html.acc-large-text body { line-height: 1.6 !important; }
html.acc-high-contrast #app { filter: contrast(1.35) brightness(1.05); }
html.acc-high-contrast #app * { text-shadow: none !important; box-shadow: none !important; }
html.acc-underline-links a { text-decoration: underline !important; }
html.acc-focus-highlight *:focus { outline: 3px solid #ed9b1f !important; outline-offset: 2px !important; }
html.acc-stop-animations *,
html.acc-stop-animations *::before,
html.acc-stop-animations *::after {
  animation-duration: 0.001ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.001ms !important;
  scroll-behavior: auto !important;
}
`

export default {
  name: 'AccessibilityWidget',
  data() {
    return {
      t: STRINGS,
      panel_open: false,
      prefs: {
        high_contrast: false,
        large_text: false,
        underline_links: false,
        focus_highlight: false,
        stop_animations: false
      },
      options: [
        { key: 'high_contrast' },
        { key: 'large_text' },
        { key: 'underline_links' },
        { key: 'focus_highlight' },
        { key: 'stop_animations' }
      ]
    }
  },
  computed: {
    anyActive() {
      return Object.values(this.prefs).some(v => v)
    }
  },
  mounted() {
    // הזרקת ה-CSS הגלובלי פעם אחת
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style')
      style.id = STYLE_ID
      style.textContent = GLOBAL_CSS
      document.head.appendChild(style)
    }
    // טעינת העדפות שמורות
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
      for (const k in this.prefs) {
        if (typeof saved[k] === 'boolean') this.prefs[k] = saved[k]
      }
    } catch (e) { /* localStorage פגום — מתעלמים */ }
    this.applyAll()
  },
  methods: {
    togglePanel() {
      this.panel_open = !this.panel_open
    },
    closePanel() {
      this.panel_open = false
    },
    toggleOption(key) {
      this.prefs[key] = !this.prefs[key]
      this.applyAll()
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.prefs))
    },
    resetAll() {
      for (const k in this.prefs) this.prefs[k] = false
      this.applyAll()
      localStorage.removeItem(STORAGE_KEY)
    },
    applyAll() {
      const html = document.documentElement
      for (const k in this.prefs) {
        html.classList.toggle('acc-' + k.replace(/_/g, '-'), this.prefs[k])
      }
    }
  }
}
</script>

<style>
/* מעוצב ב-CSS עצמאי (לא Tailwind) — הרכיב חי מחוץ ל-#app בגלל ה-Teleport,
   ו-tw- classes מקומפלים עם important:'#app' ולא חלים שם. הצבעים = ה-tokens של simpo. */
.acc-widget {
  position: fixed;
  /* צד שמאל (RTL) — מונמך מתחת לאזור ההדרים הדביקים כדי לא לכסות כפתורי פעולה */
  top: 5rem;
  left: 1rem;
  z-index: 60;
  font-family: 'Heebo', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif;
  text-align: right;
}
.acc-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid #ed9b1f;
  background: #ffffff;
  color: #ed9b1f;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  transition: background 0.2s, color 0.2s, transform 0.15s;
  padding: 0;
}
.acc-btn:hover { transform: scale(1.06); }
.acc-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(237, 155, 31, 0.45), 0 4px 16px rgba(0, 0, 0, 0.18);
}
.acc-btn-active {
  background: #ed9b1f;
  color: #ffffff;
}
.acc-panel {
  position: absolute;
  top: 64px;
  left: 0;
  width: 288px;
  background: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 16px;
  direction: rtl;
}
.acc-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.acc-panel-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #333333;
}
.acc-close {
  background: transparent;
  border: 0;
  cursor: pointer;
  color: #4a423b;
  padding: 4px;
  line-height: 0;
  border-radius: 4px;
}
.acc-close:hover { color: #ed9b1f; }
.acc-options { display: flex; flex-direction: column; gap: 10px; }
.acc-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
}
.acc-option-label {
  font-size: 14px;
  font-weight: 700;
  color: #4a423b;
}
.acc-option-row input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #ed9b1f;
  cursor: pointer;
  flex-shrink: 0;
}
.acc-option-desc {
  margin: 2px 0 0 0;
  font-size: 12px;
  color: #7d7368;
}
.acc-panel-foot {
  border-top: 1px solid #dbdbdb;
  margin-top: 12px;
  padding-top: 12px;
}
.acc-reset {
  width: 100%;
  background: transparent;
  border: 1px solid #ed9b1f;
  color: #ed9b1f;
  font-weight: 700;
  font-size: 14px;
  padding: 9px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.acc-reset:hover {
  background: #ed9b1f;
  color: #ffffff;
}
@media (max-width: 676px) {
  .acc-widget { top: 4.5rem; left: 0.75rem; }
  .acc-panel { width: 260px; }
}
</style>
